import { Dispatch, FC, PropsWithChildren, createContext, useCallback, useEffect, useState } from "react";
import { Auth, authLogout, authLoginOtp, authGetUser, authRefreshToken, authClearRedirect } from "@/api";

const SESSION_AUTH_KEY = "caprx-hcp-session";

interface SessionData {
  tokens?: Auth.LoginTokens,
  user?: Auth.GetUserResponse,
}

export type AuthState = "signedOut" | "loading" | "signedIn" | undefined;

export interface AuthenticationContextType {
  authState: AuthState;
  setAuthState: Dispatch<AuthState>;
  isUserLoggedIn: () => boolean;
  logoutUser: () => Promise<void>;
  getAuthToken: () => string | undefined;
  getUser: () => Promise<Auth.GetUserResponse | undefined>;
  setUserProvider: (providerManagementId: string, providerEnrollmentStatus: string, providerBillingNPI: string) => Promise<void>;
  refreshToken: () => Promise<void>;
  loginOtp: (
    email: string,
    emailedOtp: string,
    sessionToken: string,
  ) => Promise<string>;
  loginClear: (state: string) => Promise<string>;
}

export const AuthenticationContext = createContext<AuthenticationContextType>({
  authState: "loading",
  isUserLoggedIn: () => {
    return false;
  },
  logoutUser: async () => {
    return Promise.resolve();
  },
  getAuthToken: () => {
    return undefined;
  },
  setAuthState: () => {},
  getUser: async () => {
    return Promise.resolve(undefined);
  },
  setUserProvider: async () => {
    return Promise.resolve();
  },
  loginOtp: async () => {
    return Promise.resolve("success");
  },
  loginClear: async () => {
    return Promise.resolve("success");
  },
  refreshToken: async () => {
    return Promise.resolve();
  },
});

export const removeSession = () => {
  window.sessionStorage.removeItem(SESSION_AUTH_KEY);
};

export const AuthenticationProvider: FC<PropsWithChildren> = ({ children }) => {
  const [authState, _setAuthState] = useState<AuthState>("loading");

  const setAuthState = useCallback((newAuthState: AuthState) => {
    _setAuthState(newAuthState);

    if (newAuthState === "signedOut") {
      removeSession();
    }
  }, [authState, _setAuthState]);

  const checkLoggedInAuthState = (): AuthState => {
    if (isUserLoggedIn()) {
      return "signedIn";
    } else {
      return "signedOut";
    }
  };

  useEffect(() => {
    try {
      setAuthState(checkLoggedInAuthState());
    } catch {
      setAuthState("signedOut");
    }
  }, []);

  const getSession = (): SessionData | undefined => {
    const session = window.sessionStorage.getItem(SESSION_AUTH_KEY);
    if (session) {
      try {
        const sessionData = JSON.parse(session);
        return sessionData;
      } catch {
        console.error(`Failed to parse session ${session}`);
      }
    }
    return undefined;
  };

  const logoutUser = useCallback(async (): Promise<void> => {
    try {
      await authLogout(getAuthToken());
    } finally {
      setAuthState("signedOut");
    }

    return Promise.resolve();
  }, [setAuthState]);

  const isUserLoggedIn = (): boolean => {
    const session = getSession();
    return !!session;
  };

  const getAuthToken = (): string | undefined => {
    const session = getSession();
    if (session) {
      return session.tokens?.access_token;
    }
    return undefined;
  };

  const getAuthUsername = (): string | undefined => {
    const session = getSession();
    if (session) {
      return session.tokens?.username;
    }
    return undefined;
  };

  const getRefreshToken = (): string | undefined => {
    const session = getSession();
    if (session) {
      return session.tokens?.refresh_token;
    }
    return undefined;
  };

  const getUser = async (): Promise<Auth.GetUserResponse | undefined> => {
    const session = getSession();
    if (session?.user) {
      return session?.user;
    } else if (session && !session.user) {
      const user = await authGetUser(getAuthToken());

      window.sessionStorage.setItem(
        SESSION_AUTH_KEY,
        JSON.stringify({ ...session, user }),
      );

      return Promise.resolve(user);
    }

    return Promise.resolve(undefined);
  };

  const setUserProvider = async (providerManagementId: string, providerEnrollmentStatus: string, providerBillingNPI: string): Promise<void> => {
    const user = await getUser();
    user.provider_management_id = providerManagementId;
    user.provider_enrollment_status = providerEnrollmentStatus;
    user.provider_billing_npi = providerBillingNPI;

    const session = getSession();
    window.sessionStorage.setItem(
      SESSION_AUTH_KEY,
      JSON.stringify({ ...session, user }),
    );
  };

  const loginOtp = async (
    email: string,
    emailedOtp: string,
    sessionToken: string,
  ): Promise<string> => {
    const loginOtpResponse = await authLoginOtp({
      email: email,
      emailed_otp: emailedOtp,
      session: sessionToken,
    });

    window.sessionStorage.setItem(
      SESSION_AUTH_KEY,
      JSON.stringify({ tokens: loginOtpResponse.tokens }),
    );
    setAuthState("signedIn");

    return loginOtpResponse.result;
  };

  const loginClear = async (state: string): Promise<string> => {
    const clearRedirectResponse = await authClearRedirect({ state_id: state });

    window.sessionStorage.setItem(
      SESSION_AUTH_KEY,
      JSON.stringify({ tokens: clearRedirectResponse.tokens }),
    );
    setAuthState("signedIn");

    return "success";
  };

  const refreshToken = async (): Promise<void> => {
    const accessToken = getAuthToken();
    const refreshToken = getRefreshToken();
    const username = getAuthUsername();
    if (!accessToken || !refreshToken || !username) {
      return Promise.resolve();
    }

    const TOKEN_REFRESH_SECONDS = 30;

    // only run token refresh if the current access token is close to expiry
    const currentTimeSeconds = Math.floor(Date.now() / 1000);
    const parts = accessToken.split(".");
    const { exp } = JSON.parse(atob(parts[1]));
    const isTokenAboutToExpire = currentTimeSeconds + TOKEN_REFRESH_SECONDS >= exp;

    if (isTokenAboutToExpire) {
      window.sessionStorage.setItem(
        SESSION_AUTH_KEY,
        JSON.stringify({ tokens: await authRefreshToken(accessToken, refreshToken, username) }),
      );

      return Promise.resolve();
    }

    return Promise.resolve();
  };

  return (
    <AuthenticationContext.Provider
      value={{
        authState,
        isUserLoggedIn,
        logoutUser,
        getAuthToken,
        setAuthState,
        getUser,
        setUserProvider,
        loginOtp,
        loginClear,
        refreshToken,
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  );
};
